import { Download, Volume2, VolumeX } from "lucide-react";
import { fmtBytes } from "@/lib/studioApi";

export const STEM_COLORS = {
  vocals: "from-rose-400 to-pink-500", drums: "from-amber-300 to-orange-500", bass: "from-violet-400 to-indigo-500",
  piano: "from-sky-300 to-blue-500", guitar: "from-emerald-300 to-teal-500", other: "from-slate-300 to-slate-500",
};

function DownloadLink({ href, label, size, testId }) {
  return (
    <a href={`${href}?download=1`} download data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/80 transition-colors duration-200 hover:bg-white/[0.12] hover:text-white">
      <Download className="h-3 w-3" /> {label}{size ? <span className="text-white/40">{fmtBytes(size)}</span> : null}
    </a>
  );
}

/** One separated part: level, mute, solo, and its own WAV / MP3 download. */
export default function StemRow({ stem, mix, onChange, audible }) {
  return (
    <div data-testid={`stem-row-${stem.name}`} data-audible={audible ? "1" : "0"}
      className={`grid items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 px-4 py-3.5 transition-opacity duration-200 md:grid-cols-[minmax(0,1fr)_minmax(180px,260px)_auto] ${audible ? "" : "opacity-55"}`}>
      <div className="flex min-w-0 items-center gap-3">
        <span className={`h-9 w-1.5 shrink-0 rounded-full bg-gradient-to-b ${STEM_COLORS[stem.name] || STEM_COLORS.other}`} />
        <div className="min-w-0">
          <p data-testid={`stem-name-${stem.name}`} className="font-display text-[15px] font-semibold">{stem.label}</p>
          {stem.quiet ? <p data-testid={`stem-quiet-${stem.name}`} className="text-[11px] text-amber-200/80">Almost silent — this song has little or no {stem.label.toLowerCase()}</p> : null}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`stem-mute-${stem.name}`} aria-pressed={mix.mute} onClick={() => onChange({ mute: !mix.mute })} aria-label={mix.mute ? "Unmute" : "Mute"}
          className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border font-mono text-[11px] ${mix.mute ? "border-rose-300/60 bg-rose-500/20 text-rose-100" : "border-white/10 text-white/60 hover:text-white"}`}>
          {mix.mute ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>
        <button type="button" data-testid={`stem-solo-${stem.name}`} aria-pressed={mix.solo} onClick={() => onChange({ solo: !mix.solo })}
          className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border font-mono text-[11px] font-bold ${mix.solo ? "border-amber-300/60 bg-amber-400/20 text-amber-100" : "border-white/10 text-white/60 hover:text-white"}`}>S</button>
        <input type="range" data-testid={`stem-volume-${stem.name}`} min={0} max={100} value={mix.volume} aria-label={`${stem.label} level`}
          onChange={(e) => onChange({ volume: Number(e.target.value) })} className="w-full accent-cyan-300" />
      </div>
      <div className="flex flex-wrap gap-2">
        <DownloadLink href={stem.wav_url} label="WAV" size={stem.wav_bytes} testId={`stem-download-wav-${stem.name}`} />
        <DownloadLink href={stem.mp3_url} label="MP3" size={stem.mp3_bytes} testId={`stem-download-mp3-${stem.name}`} />
      </div>
    </div>
  );
}
